// @flow
import type { ChildrenArray, Element, Node } from 'react';
import React from 'react';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';

import { Text } from './Text.js';
import { AlgebraicWorking } from './AlgebraicWorking.js';
import BoxAndWhiskerPlot from './BoxAndWhiskerPlot.js';
import Latex from './Latex.js';
import { LatexTextAtom } from './LatexTextAtom.js';
import { CartesianPlane } from './CartesianPlane.js';
import DraftImage from './DraftImage.js';
import Image from './Image.js';
import { NumberLine } from './NumberLine.js';
import { PseudoCodeBlock } from './PseudoCodeBlock.js';
import { StemAndLeafPlot } from './StemAndLeafPlot.js';
import TabularData from './TabularData.js';
import { TwoColumnProof } from './TwoColumnProof.js';
import UglyLegacyImage from './UglyLegacyImage.js';

type MultipleChoiceProps = {|
  // Originally this component lacked a `columns` prop. We added it later
  // which is why this is optional. The original behaviour was just to
  // layout each choice in an inline manner, so that each choice took
  // up its necessary space. For example:
  //   a) 3x  b) 5x + 1  c) 2x + 1  d) 4x + 1
  // This works well for small algebraic expressions, but get's sloppy
  // for worded responses in particular. To solve this you can now provide
  // explicit columns which will make each column equal in size.
  columns?: 1 | 2 | 4,
  children: ChildrenArray<Element<typeof Choice>>,
|};
const MultipleChoice = (props: MultipleChoiceProps) => {
  const { failedValidation } = usePropsValidation(props, MultipleChoice);
  if (failedValidation) return null;

  const { columns, children } = props;

  const labelledChoices = React.Children.map(children, (child, i) =>
    React.cloneElement(child, {
      _label: String.fromCharCode(65 + i),
      _isInline: columns === undefined,
    }),
  );

  // Original inline layout behaviour
  if (columns === undefined) {
    return <div style={{ paddingTop: 15 }}>{labelledChoices}</div>;
  }

  const gap = 16;
  return (
    <div
      style={{
        paddingTop: 15,
        display: 'table',
        // Using the 'fixed' layout algorithm, in combo with an explicit
        // width of the table, ensures that the explicit widths of the cells
        // are respected (rather than expanding to accomodate the size of the
        // content)
        tableLayout: 'fixed',
        width: '100%',
      }}
    >
      {interleave(
        <div style={{ display: 'table-row', height: gap }} />,
        groupsOf(columns, labelledChoices).map((group, i) => (
          <div key={`row-${i}`} style={{ display: 'table-row' }}>
            {interleave(
              <div style={{ display: 'table-cell', width: gap }} />,
              group.map((choice) => (
                <div
                  style={{
                    display: 'table-cell',
                    // Note: It may seem odd that the widths of these cells add
                    // up to 100% despite there also being an intervleaved spacer
                    // div which takes up space. In practice the table layout algo
                    // will try to size these cells at this size, realize they won't
                    // fit and then shrinks each of them an equivalent amount. While
                    // you may think we could use calc() to do this, it seems broken
                    // in this context and has odd, non sensical behaviour afaict.
                    width: `${100 / columns}%`,
                    // TODO we should probably have some layout validation that
                    // errors if clipping is occurring.
                    overflow: 'clip',
                  }}
                >
                  {choice}
                </div>
              )),
            )}
            {/* We need to add empty cells if we don't have enough choices to
                fill the first row. Otherwise the `width` on the cells won't be
                respected and they will expand to fill the space. Only the first
                row is required as the fixed-layout algo only looks at the cells
                in the first row to determine the column widths. */}
            {i === 0 &&
              group.length < columns &&
              range(columns - group.length).map((_, i) => (
                <React.Fragment key={`padding-cell-${i}`}>
                  {/* We preprend the spacer (rather than interleaving it) as this
                      list is effectively concatenated on the end of the list of
                      choices. So we need a gap before the first empty cell. */}
                  <div style={{ display: 'table-cell', width: gap }} />
                  <div
                    style={{
                      display: 'table-cell',
                      width: `${100 / columns}%`,
                    }}
                  />
                </React.Fragment>
              ))}
          </div>
        )),
      )}
    </div>
  );
};
MultipleChoice.spec = {
  columns: t.optional(t.unionOfValues(1, 2, 4)),
  children: t.childrenArray(t.elementType(Choice)),
};

type ChoiceProps = {|
  // Label is auto-injected by us. Content authors shouldnt specify it.
  // We use the _ prefix naming convention to signify this.
  _label: string,
  // When using an inline layout, where a choice gets laid out is not
  // known. In order to ensure there is a horizontal gap between each
  // choice, as well as a vertical gap between line boxes, we have to
  // use a marginRight/Bottom. When we aren't using inline layout we
  // interleave explicit horizontal/vertical spacers since we do know
  // how they will be laid out. When we have explicit spacers we must
  // remove the margins used for the inline layout.
  _isInline: boolean,
  children: Node,
|};

function Choice(props: ChoiceProps) {
  const { failedValidation } = usePropsValidation(props, Choice);
  if (failedValidation) return null;

  const { _label, children } = props;
  return (
    <div
      style={{
        ...(props._isInline
          ? {
              marginBottom: 16,
              marginRight: 16,
            }
          : {}),
        display: 'inline-table',
        // Ensure that all Choices in a given line box are aligned with the
        // top of said line box, rather than the baseline.
        verticalAlign: 'top',
        breakInside: 'avoid',
      }}
    >
      <div
        style={{
          fontWeight: 'bold',
          display: 'table-cell',
          verticalAlign: 'top',
        }}
      >
        {_label}
      </div>
      <div style={{ display: 'table-cell' }}>
        {/* We can't specify width on the cell, as the table layout algo
            sizes the cells based on the intrinsice size of the cell contents */}
        <div style={{ width: 8 }} />
      </div>
      <div style={{ display: 'table-cell' }}>{children}</div>
    </div>
  );
}
Choice.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      // Text stuff
      t.elementType(Text),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      // Worksheet/AnswerKey bootstrapping component
      t.elementType(UglyLegacyImage),
      // Basic figures for Worksheet/AnswerKey
      t.elementType(Image),
      t.elementType(DraftImage),
      t.elementType(TabularData),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(PseudoCodeBlock),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(TwoColumnProof),
      t.elementType(AlgebraicWorking),
    ),
  ),
};

function groupsOf(n, xs) {
  const groups = [];
  for (let i = 0; i < xs.length; i += n) {
    groups.push(xs.slice(i, i + n));
  }
  return groups;
}

function interleave<T: Element<*>>(e: T, es: T[]) {
  if (es.length === 0) return [];
  const result = [es.shift()];
  while (es[0] !== undefined) {
    result.push(e);
    result.push(es.shift());
  }
  // NOTE poorly chosen numeric keys at the call site can cause issues here...
  const withKeys = result.map<T>((e, i) =>
    React.cloneElement(e, { key: e.key || i }),
  );
  return withKeys;
}

function range(n) {
  return Array.from({ length: n }, (_, i) => i);
}

export { MultipleChoice, Choice };
